import { FC } from 'react';
import { useRouter } from 'next/router';
import { cx } from '@emotion/css';
import { useTranslation } from 'next-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { containerQueries } from '@phoenix/design-system/frontend/ui/breakpoints/breakpoints';
import { PlaceholderImage } from '@phoenix/core/frontend/shared/ui/images/placeholder-image';
import { link } from '@phoenix/core/frontend/shared/util/navigation/link.utils';
import * as tracking from '@phoenix/team-m/dashboard/frontend/shared/tracking/service-appointment.events';

import { Image } from '@phoenix/team-m/dashboard/frontend/shared/components/image';
import { productCategoryAndDriveType } from '@phoenix/team-m/my-tool-shed/frontend/util/master-data';
import { ProductCard } from '@phoenix/team-m/dashboard/frontend/util/product/product.model';

import { InteractiveMenuButton } from './interactive-menu-button';
import { QuickActionLink } from './quick-action-link';

const bellIcon = getDesignSystemIcon('icon-bell-16.svg');
const wrenchIcon = getDesignSystemIcon('icon-wrench-top-right.svg');

interface ProductStageCardProps {
  product: ProductCard;
  isVisible?: boolean;
  isActive?: boolean;
}

export const ProductStageCard: FC<ProductStageCardProps> = ({ product, isActive, isVisible = false }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { 'make-dealer-appointment': dealerAppointmentFlagEnabled } = useFlags();
  const { id, serialNumber, manualUrl, image, customName, productName, serviceReminder } = product;

  const { isNextServiceDateDue, hasFutureReminderDate } = serviceReminder || {};
  const actions = serviceReminder && [
    ...(isNextServiceDateDue && dealerAppointmentFlagEnabled
      ? [
          {
            icon: wrenchIcon,
            text: t('dashboard_productStage_hint_serviceAppointment'),
            routePath: `tool-shed/service-appointment?toolId=${id}`,
            onClick: tracking.serviceAppointmentLinkClick({
              product_id: id,
              product_name: productName,
              product_category: product.productCategory?.name,
              from: 'product-stage-interactive-menu-button',
            }),
          },
        ]
      : []),
    ...(!hasFutureReminderDate
      ? [
          {
            icon: bellIcon,
            text: t('dashboard_productStage_hint_serviceReminder'),
            routePath: `tool-shed/tool-details/service-reminder?toolId=${id}`,
          },
        ]
      : []),
  ];
  const hasActions = !!actions?.length;
  const label = `${product.productName} - ${customName}`;

  return (
    <a
      href={link(`/tool-shed/tool-details?toolId=${product.id}`)}
      onClick={(e) => {
        e.preventDefault();
        isVisible && router.push(link(`/tool-shed/tool-details?toolId=${product.id}`));
      }}
      aria-hidden={isVisible ? 'false' : 'true'}
      tabIndex={isVisible ? 0 : -1}
      className={cx(
        'relative block rounded-md bg-primary-white p-4 pb-20 focus-visible:outline-primary-black focus-visible:outline-offset-2 focus-visible:outline outline-2',
        {
          'cursor-pointer': isVisible,
        }
      )}
      data-testid={`toolCard_${id}`}
      aria-label={label}
    >
      {isVisible && hasActions && (
        <InteractiveMenuButton
          isActive={isActive}
          actions={actions}
          dataTestId={`toolHint_${id}`}
          className="absolute right-4 top-4 z-10"
        />
      )}
      <div className="relative h-[116px] tablet:h-[284px]">
        {image?.src ? (
          <Image
            src={image.src}
            alt={product.productName}
            className="object-contain object-top"
            sizes={`(max-width: ${containerQueries.tablet}) 80vw, 20vw`}
            fill={true}
          />
        ) : (
          <div className={cx('flex h-full items-center justify-center bg-secondary-light opacity-50')}>
            <PlaceholderImage className="h-10 text-secondary" />
          </div>
        )}
      </div>
      <div className="mt-6 px-4" data-testid={`toolInfo_${id}`}>
        <div className="flex flex-col justify-center">
          <p className="truncate stihl-text-body-sm-reg tablet:stihl-text-body-default-reg">{productCategoryAndDriveType(product)}</p>
          <p className="mt-4 truncate stihl-text-body-default-bold tablet:!text-[18px]">{customName}</p>
          <p className="mt-1 truncate stihl-text-body-default-reg tablet:!text-[18px]">{productName}</p>
        </div>
      </div>
      {isVisible && serialNumber && (manualUrl || dealerAppointmentFlagEnabled) && (
        <div className={cx('absolute bottom-4 left-0  w-full animate-fade-in justify-center gap-x-4 ', isActive ? 'flex' : 'hidden')}>
          {dealerAppointmentFlagEnabled && (
            <QuickActionLink
              id={`serviceAppointmentLink_${id}`}
              href={link(`tool-shed/service-appointment?toolId=${id}`)}
              icon="icon-circle-wrench.svg"
              label={t('dashboard_productStage_quickAction_serviceAppointment')}
              onClick={tracking.serviceAppointmentLinkClick({
                product_id: id,
                product_name: productName,
                product_category: product.productCategory?.name,
                from: 'product-stage-quick-action-link',
              })}
            />
          )}
          {manualUrl && (
            <QuickActionLink
              id={`toolManualLink_${id}`}
              href={manualUrl}
              target="_blank"
              icon="icon-circle-note.svg"
              label={t('dashboard_productStage_quickAction_productManual')}
            />
          )}
        </div>
      )}
    </a>
  );
};
